import { PlusIcon } from '@/assets/icons'
import InviteModal from '@components/InviteModal'
import { PageColumn, PageHeader, PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import {
	useApproveJoinRequest,
	useDenyJoinRequest,
	useMe,
	useRemoveMember,
	useTrip,
	useTripJoinRequests,
	useTripMembers,
} from '@hooks'
import { countLabel } from '@/utils/plural'
import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import type { GetUserType } from '@orbit/api-types'
import {
    InviteGhostCard,
    InviteGhostLabel,
    MemberCard,
    MemberCardActions,
    MemberCardName,
    MembersGrid,
    RequestActions,
    RequestList,
    RequestMeta,
    RequestName,
    RequestRow,
    RequestsSection,
    RequestText,
    SectionHead,
} from './MembersPage.styles'
import { Avatar } from '@components/Avatar'
import { Button } from '@components/Button'
import ConfirmationModal from '@components/ConfirmationModal'
import { SectionHeading } from '@components/Heading'
import { Pill } from '@components/Pill'
import { iconSizes } from '@/theme/tokens'
import { ErrorNote } from '@components/Notice'
import { askedAgo } from '@lib/joinRequests'
import { isTripOwner, memberListName } from '@lib/members'
import { membershipFailure, removalConfirmation } from '@lib/membership'
import { getShortName } from '@/utils/userNames'

const MembersPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: membersData, isLoading: membersLoading } = useTripMembers(tripId)
	const { data: me } = useMe()
	const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
	const [removing, setRemoving] = useState<GetUserType | null>(null)

	// Join requests and removal are the owner's alone; nobody else fetches them.
	const isOwner = isTripOwner(me, trip) === true
	const { data: requestsData } = useTripJoinRequests(tripId, isOwner)
	const approve = useApproveJoinRequest(tripId)
	const deny = useDenyJoinRequest(tripId)
	const removeMember = useRemoveMember(tripId)

	if (tripLoading || membersLoading) {
		return (
			<PageScroll>
				<PageLoader label="Loading members…" />
			</PageScroll>
		)
	}

	if (!trip) {
		return (
			<PageScroll>
				<PageColumn>
					<ErrorNote>Trip not found</ErrorNote>
				</PageColumn>
			</PageScroll>
		)
	}

	const members = membersData?.members || []
	const requests = isOwner ? (requestsData?.requests ?? []) : []
	const removalCopy = removing ? removalConfirmation(removing, trip.name) : null

	const decide = (action: typeof approve, user: GetUserType, kind: 'approveRequest' | 'denyRequest') =>
		action.mutate(
			{ userId: user.id },
			{
				onError: error => {
					const message = membershipFailure({ kind }, error)
					if (message) toast.error(message)
				},
			},
		)

	const confirmRemove = () => {
		if (!removing) return
		const member = removing
		removeMember.mutate(
			{ userId: member.id },
			{
				onSuccess: () => toast.success(`${getShortName(member)} was removed`),
				onError: error => {
					const message = membershipFailure({ kind: 'remove', memberName: getShortName(member) }, error)
					if (message) toast.error(message)
				},
			},
		)
	}

	return (
		<PageScroll>
			<PageColumn>
				<PageHeader
					title="Trip Members"
					backTo={{ path: `/trips/${tripId}`, label: trip.name }}
					subtitle={countLabel(members.length, 'member')}
				/>

				{requests.length > 0 && (
					<RequestsSection aria-label="Join requests">
						<SectionHead>
							<SectionHeading>Requests</SectionHeading>
							<Pill $size="sm">{requests.length}</Pill>
						</SectionHead>
						<RequestList>
							{requests.map(({ user, requestedAt }) => (
								<RequestRow key={user.id}>
									<Avatar user={user} size="lg" tone="soft" />
									<RequestText>
										<RequestName>{memberListName(user)}</RequestName>
										<RequestMeta>{askedAgo(requestedAt)}</RequestMeta>
									</RequestText>
									<RequestActions>
										<Button
											type="button"
											variant="secondary"
											size="sm"
											aria-label={`Deny ${memberListName(user)}`}
											disabled={deny.isPending || approve.isPending}
											onClick={() => decide(deny, user, 'denyRequest')}
										>
											Deny
										</Button>
										<Button
											type="button"
											size="sm"
											aria-label={`Approve ${memberListName(user)}`}
											disabled={deny.isPending || approve.isPending}
											onClick={() => decide(approve, user, 'approveRequest')}
										>
											Approve
										</Button>
									</RequestActions>
								</RequestRow>
							))}
						</RequestList>
					</RequestsSection>
				)}

				{/* The owner's page has sections, as on iOS: Requests while any wait, and always Members. */}
				{isOwner && (
					<SectionHead>
						<SectionHeading>Members</SectionHeading>
					</SectionHead>
				)}

				<MembersGrid>
					{members.map(member => (
						<MemberCard key={member.id}>
							<Avatar user={member} size="xl" tone="soft" />
							<MemberCardName>{memberListName(member)}</MemberCardName>
							{isOwner && member.id !== trip.ownerUserId && (
								<MemberCardActions>
									<Button
										type="button"
										variant="secondary"
										size="sm"
										aria-label={`Remove ${getShortName(member)}`}
										onClick={() => setRemoving(member)}
									>
										Remove
									</Button>
								</MemberCardActions>
							)}
						</MemberCard>
					))}

					{/* The empty seat closes the grid — always there, whoever is missing. */}
					<InviteGhostCard type="button" onClick={() => setIsInviteModalOpen(true)}>
						<Avatar size="xl" fallback={<PlusIcon sx={{ fontSize: iconSizes.lg }} />} />
						<InviteGhostLabel>Invite a member</InviteGhostLabel>
					</InviteGhostCard>
				</MembersGrid>
			</PageColumn>

			<InviteModal
				isOpen={isInviteModalOpen}
				onClose={() => setIsInviteModalOpen(false)}
				tripCode={trip.code}
			/>

			<ConfirmationModal
				isOpen={removing !== null}
				onClose={() => setRemoving(null)}
				onConfirm={confirmRemove}
				title={removalCopy?.title ?? ''}
				message={removalCopy?.message ?? ''}
				confirmText={removalCopy?.confirmText}
				cancelText={removalCopy?.cancelText}
				variant="danger"
			/>
		</PageScroll>
	)
}

export default MembersPage
